import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { Auth, onIdTokenChanged, signOut, User } from '@angular/fire/auth';

export type DeauthReason = 'disabled' | 'revoked' | 'deleted' | 'invalid-token' | null;

export interface DeauthedStaffUser {
  id: string;
  email: string | null;
  username: string | null;
}

const DEAUTH_CODE_MAP: Record<string, DeauthReason> = {
  'auth/user-disabled': 'disabled',
  'auth/user-token-expired': 'revoked',
  'auth/user-not-found': 'deleted',
  'auth/invalid-user-token': 'invalid-token',
  'auth/null-user': 'invalid-token',
};

const FORCE_REFRESH_INTERVAL_MS = 10 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class AuthStateService {
  private auth = inject(Auth, { optional: true });
  private document = inject(DOCUMENT);
  private destroyRef = inject(DestroyRef);
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private nextForcedRefreshAt: number | null = null;
  private forcedRefreshInFlight: Promise<string | null> | null = null;
  private monitoredUserId: string | null = null;

  readonly currentUser = signal<User | null>(null);
  readonly isAuthReady = signal(false);
  readonly deauthReason = signal<DeauthReason>(null);
  readonly deauthedStaffUser = signal<DeauthedStaffUser | null>(null);

  constructor() {
    if (!this.auth) {
      this.isAuthReady.set(true);
      return;
    }

    const unsubscribe = onIdTokenChanged(this.auth, (user) => {
      if (user) {
        this.deauthReason.set(null);
        this.deauthedStaffUser.set(null);
      }
      this.currentUser.set(user);
      this.isAuthReady.set(true);
      this.updateRefreshSchedule(user);
    });

    this.document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.destroyRef.onDestroy(() => {
      unsubscribe();
      this.clearRefreshTimer();
      this.document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    });
  }

  /** Returns Firebase's cached token for ordinary API requests. */
  async refreshToken(): Promise<string | null> {
    const user = this.auth?.currentUser;
    if (!user) {
      return null;
    }

    const result = await user.getIdTokenResult(false);
    return result.token;
  }

  /**
   * Forces Firebase to validate the session with its server. Calls from the
   * periodic monitor and API authorization failures share one in-flight request.
   */
  forceRefresh(): Promise<string | null> {
    if (this.forcedRefreshInFlight) {
      return this.forcedRefreshInFlight;
    }

    const user = this.auth?.currentUser;
    if (!user) {
      this.stopRefreshSchedule();
      return Promise.resolve(null);
    }

    this.forcedRefreshInFlight = this.getForcedToken(user).finally(() => {
      this.forcedRefreshInFlight = null;
      this.scheduleNextForcedRefresh(true);
    });
    return this.forcedRefreshInFlight;
  }

  /** Call this after handling a deauth (e.g. once you've redirected to /login). */
  clearDeauthReason(): void {
    this.deauthReason.set(null);
    this.deauthedStaffUser.set(null);
  }

  private async getForcedToken(user: User): Promise<string | null> {
    try {
      const result = await user.getIdTokenResult(true);
      this.clearDeauthReason();
      return result.token;
    } catch (err: unknown) {
      const reason = this.getDeauthReason(err);
      if (reason) {
        this.deauthedStaffUser.set({
          id: user.uid,
          email: user.email,
          username: user.displayName,
        });
        this.deauthReason.set(reason);
        // Firebase signOut triggers AuthenticationService to clear the app
        // session (loggedIn cookie) via onAuthStateChanged.
        if (this.auth) {
          await signOut(this.auth).catch(() => {});
        }
      }
      throw err;
    }
  }

  private updateRefreshSchedule(user: User | null): void {
    if (!user) {
      this.monitoredUserId = null;
      this.stopRefreshSchedule();
      return;
    }

    if (this.monitoredUserId !== user.uid) {
      this.monitoredUserId = user.uid;
      this.nextForcedRefreshAt = Date.now() + FORCE_REFRESH_INTERVAL_MS;
    }
    this.scheduleNextForcedRefresh();
  }

  private scheduleNextForcedRefresh(resetDeadline = false): void {
    this.clearRefreshTimer();

    if (!this.auth?.currentUser || this.document.visibilityState !== 'visible') {
      return;
    }

    if (resetDeadline || this.nextForcedRefreshAt === null) {
      this.nextForcedRefreshAt = Date.now() + FORCE_REFRESH_INTERVAL_MS;
    }
    const delay = Math.max(0, this.nextForcedRefreshAt - Date.now());
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      void this.forceRefresh().catch(() => {});
    }, delay);
  }

  private readonly handleVisibilityChange = (): void => {
    if (this.document.visibilityState !== 'visible') {
      this.clearRefreshTimer();
      return;
    }

    if (!this.auth?.currentUser) {
      return;
    }

    if (this.nextForcedRefreshAt !== null && Date.now() >= this.nextForcedRefreshAt) {
      void this.forceRefresh().catch(() => {});
      return;
    }

    this.scheduleNextForcedRefresh();
  };

  private stopRefreshSchedule(): void {
    this.clearRefreshTimer();
    this.nextForcedRefreshAt = null;
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private getDeauthReason(err: unknown): DeauthReason {
    const code =
      typeof err === 'object' && err !== null && 'code' in err && typeof err.code === 'string' ? err.code : undefined;
    return code ? DEAUTH_CODE_MAP[code] : null;
  }
}
